import { SampleQuantitiesRecord } from "kmmp/frontend";
import { useReducer } from "react";
import { ExhaustiveCheckError } from "shared/errors";
import { Page } from "./enums";
import { State, Action } from "./types";
import { pagesValidationMap, reduceValidators } from "./validation";

type Reducer = (state: State, action: Action) => State;

const reduce: Reducer = (state, action) => {
    switch (action.type) {
        case "reverse": {
            return {
                ...state,
                // Don't let the page enum drop below 0
                page: Math.max(0, state.page - 1)
            }
        }

        case "advance": {
            // Don't let the page enum extend beyond its maximum
            const newPage = Math.min(Page.Confirm, state.page + 1);
            // Validate that the state as if the user had advanced to the next page
            const validators = pagesValidationMap.get(newPage) ?? [];
            const validationResult = reduceValidators(validators, state);

            if (validationResult.isOk()) {
                return {
                    ...state,
                    error: null,
                    page: newPage,
                }
            }
            return {
                ...state,
                error: validationResult.getError() as string,
            }
        }

        case "set_purchase_order_id": {
            return {
                ...state,
                purchaseOrderId: action.value,
            }
        }

        case "set_instructions": {
            return {
                ...state,
                packages: {
                    ...state.packages,
                    instructions: action.instructions,
                }
            }
        }

        case "clear_final_validation_error": {
            return {
                ...state,
                error: null,
            }
        }

        case "set_final_validation_error": {
            return {
                ...state,
                error: action.message,
            }
        }

        case "set_frame_quantity": {
            const pageKeyMap = {
                [Page.Packages]: "packages",
                [Page.Frames]: "frames",
                [Page.Corners]: "corners",
            } as const;

            if (action.page === Page.Confirm)
                throw new Error(`Page.Confirm (value ${Page.Confirm}) should not have a set_frame_quantity dispatch.`);

            const key = pageKeyMap[action.page];
            if (!key)
                throw new ExhaustiveCheckError(key);

            const updatedState: State = {
                ...state,
                [key]: {
                    ...state[key],
                    [key]: {
                        ...state[key][key],
                        [action.frame]: action.quantity
                    }
                }
            }
            console.dir(action);
            console.dir(updatedState);
            return updatedState;
        }

        default:
            throw new ExhaustiveCheckError(action);
    }
}

function mapInitialState(initialState: Partial<State>): State {
    const defaultQtyRecord: SampleQuantitiesRecord = {
        "Mahogany": 0,
    };
    let defaultState: State = {
        page: Page.Packages,
        purchaseOrderId: "",
        packages: {
            packages: defaultQtyRecord,
            instructions: "",
        },
        frames: {
            frames: defaultQtyRecord
        },
        corners: {
            corners: defaultQtyRecord
        }
    }

    if (!initialState)
        return defaultState;

    // Note: the validation here is offset, i.e. each page validates the state for its previous page
    for (let i: Page = 0; i < Math.min(initialState.page ?? Page.Confirm, Page.Confirm); i++) {
        const validators = pagesValidationMap.get(i) ?? [];
        const validationResult = reduceValidators(validators, initialState);

        if (validationResult.isOk()) {
            defaultState.page = i;

            // TODO: the assignment of state values here is fragile, has no typing, and will be prone to breaking/bugs
            switch (i) {
                case Page.Packages:
                    continue;
                case Page.Frames: {
                    defaultState.purchaseOrderId = initialState.purchaseOrderId!;
                    defaultState.packages = initialState.packages!;
                    break;
                }
                case Page.Corners: {
                    defaultState.frames = initialState.frames!;
                    break;
                }
                case Page.Confirm: {
                    defaultState.corners = initialState.corners!;
                    break;
                }
                default:
                    throw new ExhaustiveCheckError(i);
            }
        }
    }

    return defaultState;
};

export const useSampleReducer = (browserState: Partial<State>) => {
    return useReducer<Reducer, Partial<State>>(reduce, browserState, mapInitialState);
}
